import React, { useState, useEffect } from 'react';
import { adminAPI, metadataAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { Plus, Save, Trash2, Shield, ChevronDown, ChevronRight, Edit2, X, Check } from 'lucide-react';

export default function AdminConfigPage() {
  const { isAdmin, isSupportLead } = useAuth();
  const [activeTab, setActiveTab] = useState('issue-types');

  const tabs = [
    { id: 'issue-types', label: 'Issue types' },
    { id: 'features', label: 'Features' },
    { id: 'client-sla', label: 'Client SLA' },
    { id: 'assignment-rules', label: 'Assignment rules' },
    { id: 'escalation', label: 'Escalation matrix' },
    { id: 'role-mappings', label: 'Role mappings' },
    { id: 'hub-sync', label: 'Hub sync' },
  ];

  return (
    <div>
      <div className="page-header">
        <h1>Configuration</h1>
        <p>Manage metadata, SLA targets, and assignment rules</p>
      </div>
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '2px solid var(--color-border)', paddingBottom: 0, overflowX: 'auto' }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            style={{ padding: '8px 16px', border: 'none', background: 'none', fontSize: 13, fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap',
              color: activeTab === t.id ? 'var(--color-primary)' : 'var(--color-text-muted)',
              borderBottom: activeTab === t.id ? '2px solid var(--color-primary)' : '2px solid transparent', marginBottom: -2 }}>
            {t.label}
          </button>
        ))}
      </div>
      {activeTab === 'issue-types' && <IssueTypesPanel />}
      {activeTab === 'features' && <FeaturesPanel />}
      {activeTab === 'client-sla' && <ClientSLAPanel />}
      {activeTab === 'assignment-rules' && <AssignmentRulesPanel isAdmin={isAdmin} />}
      {activeTab === 'escalation' && <EscalationPanel />}
      {activeTab === 'role-mappings' && <RoleMappingPanel />}
      {activeTab === 'hub-sync' && <HubSyncPanel />}
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// Issue Types CRUD (unchanged from original)
// ══════════════════════════════════════════════════════════════
function IssueTypesPanel() {
  const [items, setItems] = useState([]);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [editingId, setEditingId] = useState(null);

  const load = async () => { try { setItems((await adminAPI.getIssueTypes()).data); } catch { toast.error('Failed to load issue types'); } };
  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    try { await adminAPI.createIssueType({ name: newName, description: newDesc, sort_order: items.length + 1 }); toast.success('Created'); setNewName(''); setNewDesc(''); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };
  const handleUpdate = async (item) => {
    try { await adminAPI.updateIssueType(item.id, { name: item.name, description: item.description, sort_order: item.sort_order }); toast.success('Updated'); setEditingId(null); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };
  const handleDelete = async (id) => {
    if (!window.confirm('Deactivate this issue type?')) return;
    try { await adminAPI.deleteIssueType(id); toast.success('Deactivated'); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="card">
      <div className="card-header"><span className="card-title">Issue types</span></div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr>
          <th style={thStyle}>Name</th><th style={thStyle}>Description</th><th style={thStyle}>Active</th><th style={{ width: 80 }}></th>
        </tr></thead>
        <tbody>
          {items.map(item => (
            <tr key={item.id} style={trStyle}>
              <td style={tdStyle}>{editingId === item.id ? <input className="form-input" value={item.name} onChange={e => setItems(items.map(i => i.id === item.id ? {...i, name: e.target.value} : i))} style={{ fontSize: 13 }} /> : item.name}</td>
              <td style={{...tdStyle, color: 'var(--color-text-muted)', fontSize: 13}}>{editingId === item.id ? <input className="form-input" value={item.description||''} onChange={e => setItems(items.map(i => i.id === item.id ? {...i, description: e.target.value} : i))} style={{ fontSize: 13 }} /> : (item.description || '-')}</td>
              <td style={tdStyle}><span style={{ fontSize: 11, color: item.is_active ? 'var(--color-success)' : 'var(--color-danger)' }}>{item.is_active ? 'Yes' : 'No'}</span></td>
              <td style={{...tdStyle, textAlign: 'right'}}>{editingId === item.id
                ? <button className="btn btn-sm btn-primary" onClick={() => handleUpdate(item)}><Save size={12} /></button>
                : <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                    <button className="btn btn-sm" onClick={() => setEditingId(item.id)}>Edit</button>
                    <button className="btn btn-sm btn-danger" onClick={() => handleDelete(item.id)}><Trash2 size={12} /></button>
                  </div>}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ padding: 16, borderTop: '1px solid var(--color-border)', display: 'flex', gap: 8, alignItems: 'flex-end' }}>
        <div style={{ flex: 1 }}><label className="form-label" style={{ fontSize: 12 }}>New issue type</label><input className="form-input" value={newName} onChange={e => setNewName(e.target.value)} placeholder="Name" style={{ fontSize: 13 }} /></div>
        <div style={{ flex: 2 }}><label className="form-label" style={{ fontSize: 12 }}>Description</label><input className="form-input" value={newDesc} onChange={e => setNewDesc(e.target.value)} placeholder="Description (optional)" style={{ fontSize: 13 }} /></div>
        <button className="btn btn-primary btn-sm" onClick={handleCreate} disabled={!newName.trim()}><Plus size={14} /> Add</button>
      </div>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// Features — TREE VIEW with edit/delete (REWRITTEN)
// ══════════════════════════════════════════════════════════════
function FeaturesPanel() {
  const [allFeatures, setAllFeatures] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [newParent, setNewParent] = useState('');
  const [newChild, setNewChild] = useState('');
  const [newChildParentId, setNewChildParentId] = useState(null);

  const load = async () => {
    try { setAllFeatures((await adminAPI.getFeatures()).data); }
    catch { toast.error('Failed to load features'); }
  };
  useEffect(() => { load(); }, []);

  // Group into parent → children
  const parents = allFeatures.filter(f => !f.parent_id && f.is_active);
  const childrenOf = (parentId) => allFeatures.filter(f => f.parent_id === parentId && f.is_active);

  const toggleExpand = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  const expandAll = () => { const m = {}; parents.forEach(p => m[p.id] = true); setExpanded(m); };
  const collapseAll = () => setExpanded({});

  const startEdit = (f) => { setEditingId(f.id); setEditName(f.name); };
  const cancelEdit = () => { setEditingId(null); setEditName(''); };
  const saveEdit = async () => {
    if (!editName.trim()) return;
    const f = allFeatures.find(x => x.id === editingId);
    try { await adminAPI.updateFeature(editingId, { name: editName, parent_id: f?.parent_id || null, sort_order: f?.sort_order || 0 }); toast.success('Updated'); cancelEdit(); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleAddParent = async () => {
    if (!newParent.trim()) return;
    try { await adminAPI.createFeature({ name: newParent, sort_order: parents.length + 1 }); toast.success('Feature added'); setNewParent(''); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleAddChild = async (parentId) => {
    if (!newChild.trim()) return;
    const children = childrenOf(parentId);
    try { await adminAPI.createFeature({ name: newChild, parent_id: parentId, sort_order: children.length + 1 }); toast.success('Sub-feature added'); setNewChild(''); setNewChildParentId(null); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async (f) => {
    const label = f.parent_id ? 'sub-feature' : 'feature (and all sub-features)';
    if (!window.confirm(`Deactivate this ${label}?`)) return;
    try { await adminAPI.deleteFeature(f.id); toast.success('Deactivated'); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="card">
      <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span className="card-title">Features & sub-features</span>
        <div style={{ display: 'flex', gap: 6 }}>
          <button className="btn btn-sm" onClick={expandAll}>Expand all</button>
          <button className="btn btn-sm" onClick={collapseAll}>Collapse all</button>
        </div>
      </div>

      <div style={{ padding: 16 }}>
        {parents.length === 0 ? (
          <p style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>No features. Add one below.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {parents.map(p => {
              const children = childrenOf(p.id);
              const isExpanded = expanded[p.id];
              return (
                <div key={p.id}>
                  {/* Parent row */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 4px', background: isExpanded ? 'var(--color-bg-subtle, #f8f9fa)' : 'transparent', borderRadius: 6 }}>
                    <button onClick={() => toggleExpand(p.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: 'var(--color-text-muted)' }}>
                      {children.length > 0 ? (isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />) : <span style={{ width: 14, display: 'inline-block' }} />}
                    </button>

                    {editingId === p.id ? (
                      <>
                        <input className="form-input" value={editName} onChange={e => setEditName(e.target.value)} style={{ fontSize: 13, flex: 1, padding: '4px 8px' }} autoFocus onKeyDown={e => e.key === 'Enter' && saveEdit()} />
                        <button className="btn btn-sm btn-primary" onClick={saveEdit}><Check size={12} /></button>
                        <button className="btn btn-sm" onClick={cancelEdit}><X size={12} /></button>
                      </>
                    ) : (
                      <>
                        <span style={{ fontWeight: 600, fontSize: 14, flex: 1 }}>{p.name}</span>
                        <span style={{ fontSize: 11, color: 'var(--color-text-muted)', marginRight: 8 }}>{children.length} sub</span>
                        <button className="btn btn-sm" onClick={() => startEdit(p)} title="Edit"><Edit2 size={12} /></button>
                        <button className="btn btn-sm btn-danger" onClick={() => handleDelete(p)} title="Deactivate"><Trash2 size={12} /></button>
                        <button className="btn btn-sm" onClick={() => { setNewChildParentId(p.id); setNewChild(''); toggleExpand(p.id); }} title="Add sub-feature" style={{ color: 'var(--color-primary)' }}><Plus size={12} /></button>
                      </>
                    )}
                  </div>

                  {/* Children */}
                  {isExpanded && (
                    <div style={{ paddingLeft: 28, borderLeft: '2px solid var(--color-border)', marginLeft: 10, marginBottom: 4 }}>
                      {children.map(c => (
                        <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 4px', fontSize: 13 }}>
                          {editingId === c.id ? (
                            <>
                              <input className="form-input" value={editName} onChange={e => setEditName(e.target.value)} style={{ fontSize: 12, flex: 1, padding: '3px 6px' }} autoFocus onKeyDown={e => e.key === 'Enter' && saveEdit()} />
                              <button className="btn btn-sm btn-primary" onClick={saveEdit}><Check size={10} /></button>
                              <button className="btn btn-sm" onClick={cancelEdit}><X size={10} /></button>
                            </>
                          ) : (
                            <>
                              <span style={{ flex: 1, color: 'var(--color-text)' }}>{c.name}</span>
                              <button className="btn btn-sm" onClick={() => startEdit(c)} style={{ padding: '2px 4px' }}><Edit2 size={10} /></button>
                              <button className="btn btn-sm btn-danger" onClick={() => handleDelete(c)} style={{ padding: '2px 4px' }}><Trash2 size={10} /></button>
                            </>
                          )}
                        </div>
                      ))}
                      {/* Inline add sub-feature */}
                      {newChildParentId === p.id && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 4px' }}>
                          <input className="form-input" value={newChild} onChange={e => setNewChild(e.target.value)} placeholder="New sub-feature name" style={{ fontSize: 12, flex: 1, padding: '3px 6px' }} autoFocus onKeyDown={e => { if (e.key === 'Enter') handleAddChild(p.id); if (e.key === 'Escape') setNewChildParentId(null); }} />
                          <button className="btn btn-sm btn-primary" onClick={() => handleAddChild(p.id)} disabled={!newChild.trim()}><Plus size={10} /></button>
                          <button className="btn btn-sm" onClick={() => setNewChildParentId(null)}><X size={10} /></button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Add parent feature */}
        <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--color-border)', display: 'flex', gap: 8 }}>
          <input className="form-input" value={newParent} onChange={e => setNewParent(e.target.value)} placeholder="New top-level feature" style={{ fontSize: 13, flex: 1 }} onKeyDown={e => e.key === 'Enter' && handleAddParent()} />
          <button className="btn btn-primary btn-sm" onClick={handleAddParent} disabled={!newParent.trim()}><Plus size={14} /> Add feature</button>
        </div>
      </div>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// Client SLA — Issue SLA + Incident Management Performance
// ══════════════════════════════════════════════════════════════
function ClientSLAPanel() {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [slaRows, setSlaRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { metadataAPI.getClients().then(r => setClients(r.data)).catch(() => {}); }, []);

  const loadSLA = async (clientId) => {
    setLoading(true);
    try { setSlaRows((await adminAPI.getClientSLA(clientId)).data); }
    catch { toast.error('Failed to load SLA'); }
    finally { setLoading(false); }
  };
  const handleClientChange = (id) => { setSelectedClient(id); if (id) loadSLA(id); else setSlaRows([]); };
  const handleSeedDefaults = async () => {
    try { const r = await adminAPI.seedDefaultSLA(selectedClient); toast.success(`Seeded ${r.data.seeded} SLA defaults`); loadSLA(selectedClient); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };
  const handleSaveSLA = async (row) => {
    try { await adminAPI.upsertClientSLA(selectedClient, row); toast.success('SLA saved'); loadSLA(selectedClient); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const sevNames = { 1: 'Critical', 2: 'High', 3: 'Medium', 4: 'Low' };

  return (
    <div className="card">
      <div className="card-header"><span className="card-title">Client SLA configuration</span></div>
      <div style={{ padding: '0 16px 16px', display: 'flex', gap: 12, alignItems: 'flex-end' }}>
        <div style={{ flex: 1 }}>
          <label className="form-label">Client</label>
          <select className="form-select" value={selectedClient} onChange={e => handleClientChange(e.target.value)}>
            <option value="">Select client...</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.client_name} ({c.client_code})</option>)}
          </select>
        </div>
        {selectedClient && <button className="btn btn-sm" onClick={handleSeedDefaults}>Seed defaults</button>}
      </div>

      {selectedClient && !loading && slaRows.length > 0 && (
        <>
          {/* Table 1: Issue SLA */}
          <div style={{ padding: '0 16px 16px' }}>
            <h4 style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: 'var(--color-text-muted)' }}>Issue SLA</h4>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead><tr>
                <th style={thStyle}>Severity</th>
                <th style={thStyle}>Response (hrs)</th>
                <th style={thStyle}>Workaround (hrs)</th>
                <th style={thStyle}>Permanent fix (hrs)</th>
                <th style={thStyle}>Fix response</th>
                <th style={thStyle}>Errors/year</th>
                <th style={{ width: 50 }}></th>
              </tr></thead>
              <tbody>
                {slaRows.map((row, idx) => (
                  <tr key={row.severity_level} style={trStyle}>
                    <td style={{...tdStyle, fontWeight: 600}}>{sevNames[row.severity_level]}</td>
                    <td style={tdStyle}><input type="number" className="form-input" style={numInput} value={row.response_time_hours} onChange={e => setSlaRows(slaRows.map((r,i) => i===idx ? {...r, response_time_hours: parseFloat(e.target.value)} : r))} /></td>
                    <td style={tdStyle}><input type="number" className="form-input" style={numInput} value={row.fix_time_workaround_hours||''} onChange={e => setSlaRows(slaRows.map((r,i) => i===idx ? {...r, fix_time_workaround_hours: e.target.value ? parseFloat(e.target.value) : null} : r))} /></td>
                    <td style={tdStyle}><input type="number" className="form-input" style={numInput} value={row.fix_time_permanent_hours} onChange={e => setSlaRows(slaRows.map((r,i) => i===idx ? {...r, fix_time_permanent_hours: parseFloat(e.target.value)} : r))} /></td>
                    <td style={tdStyle}><input className="form-input" style={{...numInput, width: 140}} value={row.fix_response||''} onChange={e => setSlaRows(slaRows.map((r,i) => i===idx ? {...r, fix_response: e.target.value} : r))} /></td>
                    <td style={tdStyle}><input type="number" className="form-input" style={{...numInput, width: 50}} value={row.acceptable_errors_year||''} onChange={e => setSlaRows(slaRows.map((r,i) => i===idx ? {...r, acceptable_errors_year: e.target.value ? parseInt(e.target.value) : null} : r))} /></td>
                    <td style={tdStyle}><button className="btn btn-sm btn-primary" onClick={() => handleSaveSLA(row)}><Save size={12} /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Table 2: Incident Management Performance */}
          <div style={{ padding: '0 16px 16px' }}>
            <h4 style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: 'var(--color-text-muted)' }}>Incident Management Performance</h4>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead><tr>
                <th style={thStyle}>Priority</th>
                <th style={thStyle}>Response threshold (%)</th>
                <th style={thStyle}>Response duration (hrs)</th>
                <th style={thStyle}>Resolution threshold (%)</th>
                <th style={thStyle}>Resolution duration (hrs)</th>
                <th style={{ width: 50 }}></th>
              </tr></thead>
              <tbody>
                {slaRows.map((row, idx) => (
                  <tr key={`imp-${row.severity_level}`} style={trStyle}>
                    <td style={{...tdStyle, fontWeight: 600}}>P{row.severity_level} – {sevNames[row.severity_level]}</td>
                    <td style={tdStyle}><input type="number" className="form-input" style={numInput} value={row.response_threshold_pct} onChange={e => setSlaRows(slaRows.map((r,i) => i===idx ? {...r, response_threshold_pct: parseFloat(e.target.value)} : r))} /></td>
                    <td style={{...tdStyle, color: 'var(--color-text-muted)'}}>{row.response_time_hours}</td>
                    <td style={tdStyle}><input type="number" className="form-input" style={numInput} value={row.resolution_threshold_pct} onChange={e => setSlaRows(slaRows.map((r,i) => i===idx ? {...r, resolution_threshold_pct: parseFloat(e.target.value)} : r))} /></td>
                    <td style={{...tdStyle, color: 'var(--color-text-muted)'}}>{row.fix_time_permanent_hours}</td>
                    <td style={tdStyle}><button className="btn btn-sm btn-primary" onClick={() => handleSaveSLA(row)}><Save size={12} /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 4 }}>
              Response/resolution durations are from Issue SLA above. Edit thresholds here, durations above.
            </p>
          </div>
        </>
      )}

      {selectedClient && !loading && slaRows.length === 0 && (
        <p style={{ color: 'var(--color-text-muted)', fontSize: 13, padding: '0 16px 16px' }}>No SLA configured. Click "Seed defaults" to populate from spec.</p>
      )}
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// Assignment Rules — FIXED: independent loading, CTO in roles
// ══════════════════════════════════════════════════════════════
function AssignmentRulesPanel({ isAdmin }) {
  const [rules, setRules] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loadError, setLoadError] = useState(null);

  const load = async () => {
    setLoadError(null);
    try { setGroups((await metadataAPI.getGroups()).data); }
    catch (err) { setLoadError(`Groups: ${err.response?.data?.detail || err.message}`); }
    try { setRules((await adminAPI.getAssignmentRules()).data); }
    catch (err) { setLoadError(prev => (prev ? prev + '; ' : '') + `Rules: ${err.response?.data?.detail || err.message}`); }
  };
  useEffect(() => { load(); }, []);

  if (!isAdmin) {
    return <div className="card" style={{ padding: 40, textAlign: 'center', color: 'var(--color-text-muted)' }}><Shield size={24} style={{ marginBottom: 8 }} /><p>Assignment rules are admin-only.</p></div>;
  }

  const roles = ['CTO', 'SUPP_LEAD', 'QA_LEAD', 'ENGG_LEAD', 'OPS_LEAD', 'ETL_LEAD', 'ADMIN'];
  const roleNames = { CTO: 'CTO', SUPP_LEAD: 'Support Lead', QA_LEAD: 'QA Lead', ENGG_LEAD: 'Engineering Lead', OPS_LEAD: 'DevOps Lead', ETL_LEAD: 'ETL Lead', ADMIN: 'Admin' };
  const isChecked = (role, gid) => rules.some(r => r.assigner_role === role && r.target_group_id === gid && r.can_assign);
  const handleToggle = async (role, gid) => {
    try { await adminAPI.upsertAssignmentRule({ assigner_role: role, target_group_id: gid, can_assign: !isChecked(role, gid) }); load(); }
    catch { toast.error('Failed to update'); }
  };

  return (
    <div className="card">
      <div className="card-header"><span className="card-title">Assignment rules matrix</span><button className="btn btn-sm" onClick={load}>Refresh</button></div>
      {loadError && <div style={{ padding: '8px 16px', background: '#fef2f2', color: '#dc2626', fontSize: 12 }}>{loadError}</div>}
      <div style={{ padding: 16, overflowX: 'auto' }}>
        {groups.length === 0 ? (
          <p style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>No groups loaded. Run <code>python -m app.seed</code> then <code>python -m app.seed_staff</code>.</p>
        ) : (
          <>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead><tr>
                <th style={thStyle}>Assigner role</th>
                {groups.map(g => <th key={g.id} style={{...thStyle, textAlign: 'center'}}>{g.group_name}<div style={{ fontSize: 10, fontWeight: 400, color: 'var(--color-text-muted)' }}>{g.members?.length || 0} members</div></th>)}
              </tr></thead>
              <tbody>
                {roles.map(role => (
                  <tr key={role} style={trStyle}>
                    <td style={{...tdStyle, fontWeight: 500}}>{roleNames[role] || role}</td>
                    {groups.map(g => (
                      <td key={g.id} style={{ textAlign: 'center', padding: '8px' }}>
                        <input type="checkbox" checked={isChecked(role, g.id)} onChange={() => handleToggle(role, g.id)} style={{ width: 16, height: 16, cursor: 'pointer' }} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            <p style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 12 }}>
              <strong>Policy:</strong> Admin and CTO can assign to any group and any individual.
              Support Lead can assign to groups. Team leads (QA, Engg, DevOps, ETL) can assign individuals within their own group.
            </p>
          </>
        )}
      </div>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// Escalation Matrix (unchanged from Phase 3)
// ══════════════════════════════════════════════════════════════
function EscalationPanel() {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [rows, setRows] = useState([]);
  const [localEdits, setLocalEdits] = useState({});  // key: "P1-2" -> minutes value
  const [dirtyRows, setDirtyRows] = useState(new Set());  // which priority rows have unsaved changes
  const [loading, setLoading] = useState(false);

  useEffect(() => { metadataAPI.getClients().then(r => setClients(r.data)).catch(() => {}); }, []);
  const loadData = async (cid) => { setLoading(true); setLocalEdits({}); setDirtyRows(new Set()); try { setRows((await adminAPI.getEscalation(cid)).data); } catch { toast.error('Failed'); } finally { setLoading(false); } };
  const handleClientChange = (id) => { setSelectedClient(id); if (id) loadData(id); else setRows([]); };

  const priorities = ['P1', 'P2', 'P3', 'P4'];
  const levels = [1, 2, 3, 4];
  const defaults = { P1: { 1: 15, 2: 30, 3: 60, 4: 120 }, P2: { 1: 60, 2: 120, 3: 240, 4: 360 }, P3: { 1: 360, 2: 720, 3: 1440, 4: 2880 }, P4: { 1: 360, 2: 720, 3: 1440, 4: 2880 } };
  const levelRoles = { 1: 'Support Lead', 2: 'PC', 3: 'CTO', 4: 'CEO' };

  const getThreshold = (p, l) => {
    const key = `${p}-${l}`;
    if (key in localEdits) return localEdits[key];
    return rows.find(r => r.priority === p && r.escalation_level === l)?.threshold_minutes ?? '';
  };

  const handleChange = (p, l, value) => {
    const key = `${p}-${l}`;
    setLocalEdits(prev => ({ ...prev, [key]: value }));
    setDirtyRows(prev => new Set(prev).add(p));
  };

  const handleSaveRow = async (p) => {
    let saved = 0;
    for (const l of levels) {
      const key = `${p}-${l}`;
      const val = key in localEdits ? localEdits[key] : getThreshold(p, l);
      if (val === '' || val === undefined) continue;
      try { await adminAPI.upsertEscalation(selectedClient, { priority: p, escalation_level: l, threshold_minutes: parseInt(val) }); saved++; }
      catch { toast.error(`Failed to save ${p} L${l}`); }
    }
    if (saved > 0) {
      toast.success(`${p} thresholds saved`);
      // Clear dirty state for this row
      setDirtyRows(prev => { const next = new Set(prev); next.delete(p); return next; });
      // Remove local edits for this row
      setLocalEdits(prev => { const next = { ...prev }; levels.forEach(l => delete next[`${p}-${l}`]); return next; });
      loadData(selectedClient);
    }
  };

  const handleSeedDefaults = async () => {
    let count = 0;
    for (const p of priorities) for (const l of levels) {
      if (!rows.find(r => r.priority === p && r.escalation_level === l)) {
        try { await adminAPI.upsertEscalation(selectedClient, { priority: p, escalation_level: l, threshold_minutes: defaults[p][l] }); count++; } catch {}
      }
    }
    toast.success(`Seeded ${count} thresholds`); loadData(selectedClient);
  };

  return (
    <div className="card">
      <div className="card-header"><span className="card-title">Client escalation matrix</span></div>
      <div style={{ padding: '0 16px 16px', display: 'flex', gap: 12, alignItems: 'flex-end' }}>
        <div style={{ flex: 1 }}><label className="form-label">Client</label><select className="form-select" value={selectedClient} onChange={e => handleClientChange(e.target.value)}><option value="">Select client...</option>{clients.map(c => <option key={c.id} value={c.id}>{c.client_name} ({c.client_code})</option>)}</select></div>
        {selectedClient && <button className="btn btn-sm" onClick={handleSeedDefaults}>Seed defaults</button>}
      </div>
      {selectedClient && !loading && (
        <div style={{ padding: '0 16px 16px', overflowX: 'auto' }}>
          <p style={{ fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 8 }}>Thresholds in minutes. Edit values then click Save for that row.</p>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr><th style={thStyle}>Priority</th>{levels.map(l => <th key={l} style={{...thStyle, textAlign: 'center'}}>Level {l}<div style={{ fontSize: 10, fontWeight: 400, color: 'var(--color-text-muted)' }}>{levelRoles[l]}</div></th>)}<th style={{ width: 70 }}></th></tr></thead>
            <tbody>{priorities.map(p => <tr key={p} style={trStyle}><td style={{...tdStyle, fontWeight: 600}}>{p}</td>{levels.map(l => <td key={l} style={{ textAlign: 'center', padding: '4px 8px' }}><input type="number" className="form-input" style={{...numInput, textAlign: 'center'}} value={getThreshold(p, l)} placeholder={String(defaults[p]?.[l] || '')} onChange={e => handleChange(p, l, e.target.value)} /></td>)}<td style={{ textAlign: 'center', padding: '4px 8px' }}><button className="btn btn-sm btn-primary" onClick={() => handleSaveRow(p)} disabled={!dirtyRows.has(p)} title="Save row"><Save size={12} /></button></td></tr>)}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// Role Mapping — FIXED: no Priority, just role-to-role mapping
// ══════════════════════════════════════════════════════════════
function RoleMappingPanel() {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newRow, setNewRow] = useState({ priority: 'P1', axs_role: '', client_role_name: '', contact_name: '', contact_email: '' });

  useEffect(() => { metadataAPI.getClients().then(r => setClients(r.data)).catch(() => {}); }, []);
  const loadData = async (cid) => { setLoading(true); try { setRows((await adminAPI.getRoleMappings(cid)).data); } catch { toast.error('Failed'); } finally { setLoading(false); } };
  const handleClientChange = (id) => { setSelectedClient(id); if (id) loadData(id); else setRows([]); };

  const axsRoles = [
    { value: 'Support Lead', label: 'Support Lead' },
    { value: 'PC', label: 'Project Coordinator' },
    { value: 'CTO', label: 'CTO' },
    { value: 'CEO', label: 'CEO' },
  ];

  const handleAdd = async () => {
    if (!newRow.axs_role) { toast.error('Axslogic role required'); return; }
    try { await adminAPI.upsertRoleMapping(selectedClient, newRow); toast.success('Role mapping added'); setNewRow({ priority: 'P1', axs_role: '', client_role_name: '', contact_name: '', contact_email: '' }); loadData(selectedClient); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="card">
      <div className="card-header"><span className="card-title">Client escalation role mapping</span></div>
      <div style={{ padding: '0 16px' }}>
        <p style={{ fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 12 }}>
          Maps Axslogic roles to client-side contacts for escalation communication.
        </p>
      </div>
      <div style={{ padding: '0 16px 16px' }}>
        <label className="form-label">Client</label>
        <select className="form-select" value={selectedClient} onChange={e => handleClientChange(e.target.value)} style={{ maxWidth: 400 }}>
          <option value="">Select client...</option>
          {clients.map(c => <option key={c.id} value={c.id}>{c.client_name} ({c.client_code})</option>)}
        </select>
      </div>

      {selectedClient && !loading && (
        <div style={{ padding: '0 16px 16px' }}>
          {rows.length > 0 && (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 16 }}>
              <thead><tr>
                <th style={thStyle}>Axslogic role</th>
                <th style={thStyle}>Client role</th>
                <th style={thStyle}>Contact name</th>
                <th style={thStyle}>Contact email</th>
              </tr></thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.id} style={trStyle}>
                    <td style={{...tdStyle, fontWeight: 500}}>{r.axs_role}</td>
                    <td style={tdStyle}>{r.client_role_name || '-'}</td>
                    <td style={tdStyle}>{r.contact_name || '-'}</td>
                    <td style={{...tdStyle, fontSize: 12}}>{r.contact_email || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div style={{ background: 'var(--color-bg-subtle, #f9fafb)', borderRadius: 8, padding: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>Add role mapping</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr auto', gap: 8, alignItems: 'end' }}>
              <div><label className="form-label" style={{ fontSize: 11 }}>Axslogic role</label>
                <select className="form-select" style={{ fontSize: 12 }} value={newRow.axs_role} onChange={e => setNewRow({...newRow, axs_role: e.target.value})}>
                  <option value="">Select...</option>{axsRoles.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select></div>
              <div><label className="form-label" style={{ fontSize: 11 }}>Client role</label>
                <input className="form-input" style={{ fontSize: 12 }} placeholder="e.g. Business SPOC" value={newRow.client_role_name} onChange={e => setNewRow({...newRow, client_role_name: e.target.value})} /></div>
              <div><label className="form-label" style={{ fontSize: 11 }}>Contact name</label>
                <input className="form-input" style={{ fontSize: 12 }} value={newRow.contact_name} onChange={e => setNewRow({...newRow, contact_name: e.target.value})} /></div>
              <div><label className="form-label" style={{ fontSize: 11 }}>Email</label>
                <input className="form-input" style={{ fontSize: 12 }} value={newRow.contact_email} onChange={e => setNewRow({...newRow, contact_email: e.target.value})} /></div>
              <button className="btn btn-primary btn-sm" onClick={handleAdd} disabled={!newRow.axs_role}><Plus size={14} /></button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// Hub Sync — trigger + status log
// ══════════════════════════════════════════════════════════════
function HubSyncPanel() {
  const [logs, setLogs] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadStatus = async () => { try { setLogs((await adminAPI.getHubSyncStatus()).data.logs || []); } catch {} finally { setLoading(false); } };
  useEffect(() => { loadStatus(); }, []);

  const handleSync = async () => {
    setSyncing(true);
    try { const r = await adminAPI.triggerHubSync(); toast.success(`Hub sync ${r.data.status}: ${r.data.clients||0} clients, ${r.data.modules||0} modules, ${r.data.marts||0} marts`); await loadStatus(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Hub sync failed'); }
    finally { setSyncing(false); }
  };

  const statusColor = (s) => s === 'SUCCESS' ? '#059669' : s === 'FAILED' ? '#dc2626' : s === 'RUNNING' ? '#d97706' : '#6b7280';

  return (
    <div className="card">
      <div className="card-header"><span className="card-title">Hub sync</span></div>
      <div style={{ padding: 16 }}>
        <p style={{ fontSize: 13, color: 'var(--color-text-muted)', marginBottom: 16 }}>Pulls client metadata (modules, marts) from the Winnow Hub API. Scheduled weekdays 04:00–06:00 UTC. Uses mock data when HUB_API_KEY is not configured.</p>
        <button className="btn btn-primary" onClick={handleSync} disabled={syncing}>{syncing ? '⟳ Syncing...' : '⟳ Trigger Hub sync now'}</button>
        <div style={{ marginTop: 24 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Recent sync runs</div>
          {loading ? <p style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>Loading...</p> : logs.length === 0 ? <p style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>No sync runs yet.</p> : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead><tr><th style={thStyle}>Started</th><th style={thStyle}>Status</th><th style={{...thStyle, textAlign: 'center'}}>Clients</th><th style={{...thStyle, textAlign: 'center'}}>Modules</th><th style={{...thStyle, textAlign: 'center'}}>Marts</th><th style={thStyle}>Error</th></tr></thead>
              <tbody>{logs.map(l => (
                <tr key={l.id} style={trStyle}>
                  <td style={{...tdStyle, fontSize: 12}}>{l.started_at ? new Date(l.started_at).toLocaleString() : '-'}</td>
                  <td style={tdStyle}><span style={{ fontWeight: 600, color: statusColor(l.status) }}>{l.status}</span></td>
                  <td style={{...tdStyle, textAlign: 'center'}}>{l.clients_synced}</td>
                  <td style={{...tdStyle, textAlign: 'center'}}>{l.modules_synced}</td>
                  <td style={{...tdStyle, textAlign: 'center'}}>{l.marts_synced}</td>
                  <td style={{...tdStyle, fontSize: 11, color: 'var(--color-danger)'}}>{l.error_message || '-'}</td>
                </tr>
              ))}</tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}


// ── Shared styles ──
const thStyle = { textAlign: 'left', padding: '6px 8px', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', color: 'var(--color-text-muted)' };
const tdStyle = { padding: '6px 8px' };
const trStyle = { borderBottom: '1px solid var(--color-border)' };
const numInput = { width: 70, fontSize: 13, padding: '4px 8px' };
